//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse  } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import PondSectionModel from './pondSection.model';

/**
 * @service PondSectionService
 * @description Service for managing Pond Sections within the aquaculture system.
 * A "Pond Section" is a logical grouping of ponds within a specific Farm Site. Sections are often used
 * to organize large farms into manageable units, assign specific technicians to groups of ponds,
 * or aggregate reporting data.
 *
 * This service acts as the interface to the `/aqu/pondsection/` backend endpoint.
 *
 * **Key Responsibilities:**
 * 1. **Section Management**: Retrieving sections associated with farm sites.
 * 2. **Personnel Assignment**: Linking specific users (Technicians/Managers) to specific sections for responsibility tracking.
 * 3. **Data Aggregation**: Fetching counts and summaries at the section level.
 * 4. **Resource Management**: Handling HTTP request lifecycles to prevent memory leaks.
 *
 * **Data Flow:**
 * - **Retrieval**: Components request sections by Farm Site ID.
 * - **Assignment**: Admin/Manager assigns a person ID to a list of section IDs via `assignPersonToSection`.
 *
 * @feature 1. Retrieve sections by Farm Site.
 * @feature 2. Assign personnel to sections.
 * @feature 3. Get section counts for dashboards.
 * @feature 4. Robust management of concurrent HTTP requests.
 *
 * @architecture
 * Extends base Service<PondSectionModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /aqu/pondsection/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class PondSectionService extends Service<PondSectionModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/aqu/pondsection/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/aqu/pondsection/');
  }

  /**
   * @method getSectionsByFarmsiteId
   * @async
   * @description Fetch all sections belonging to a specific farm site ID.
   *
   * @param {string} farmsiteId - The unique identifier of the farm site.
   * @returns {Promise<Array<PondSectionModel>>} Promise resolving to the list of sections.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getAllSections() {
    return new Promise<Array<PondSectionModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<PondSectionModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=10000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getSectionsByFarmsiteId
   * @async
   * @description Fetch all sections belonging to a specific farm site ID.
   *
   * @param {string} farmsiteId - The unique identifier of the farm site.
   * @returns {Promise<Array<PondSectionModel>>} Promise resolving to the list of sections.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getSectionsByFarmsiteId(farmsiteId: string) {
    return new Promise<Array<PondSectionModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<PondSectionModel>>(`${this.baseUrl}${this.serviceUrl}farmSiteID/${farmsiteId}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
